import { CheckIcon, CopyIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn, copyToClipboard } from "@/lib/utils";

/* Hallmark · component: copy button · genre: modern-minimal · theme: project tokens
 * states: default · hover · focus-visible · active · copied
 * contrast: pass — muted-foreground on card, emerald-600 for the copied tick
 */

/**
 * Copies one short value — a registration number, an invoice number.
 *
 * Silent success, per the house rule: the icon becomes a tick for a moment and
 * nothing else happens. A toast for something this small would be louder than
 * the action itself, and these sit inside table rows where several may appear at
 * once.
 *
 * Always visible, never hover-only. A control that appears on hover cannot be
 * found by anyone who does not already know it is there, and is unreachable on
 * touch.
 */
export function CopyButton({
	value,
	label,
	className,
}: {
	value: string;
	/** Names the thing being copied, for screen readers and the native tooltip. */
	label: string;
	className?: string;
}) {
	const [copied, setCopied] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout>>();

	// Clicking a second row while the first is still ticking would otherwise leave
	// a timer running against an unmounted button.
	useEffect(() => () => clearTimeout(timer.current), []);

	const copy = async () => {
		// The shared helper falls back to a hidden textarea where the Clipboard API
		// is unavailable, and swallows a denied permission — not worth interrupting
		// anyone over, since the value is on screen and selectable either way.
		if (!(await copyToClipboard(value))) return;

		setCopied(true);
		clearTimeout(timer.current);
		timer.current = setTimeout(() => setCopied(false), 1500);
	};

	return (
		<button
			type="button"
			onClick={copy}
			aria-label={label}
			title={label}
			className={cn(
				"shrink-0 rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 active:scale-95",
				className,
			)}
		>
			{copied ? (
				<CheckIcon className="size-3.5 text-emerald-600" />
			) : (
				<CopyIcon className="size-3.5" />
			)}
		</button>
	);
}
